import { withWindow } from '@shared/utils/withWindow';
import { useCallback } from 'react';
import { useRouteMatch, useHistory } from '@react-router';
import { GiftBasketTab, MinimumValidRegistryOrder } from './RegistryGiftBasketWidget.types';
import { useFeatureFlagsContext } from '@shared/core/featureFlags';
import { ADMIN_MANAGE_PAGE_TAB_QUERY_PARAM } from '../../util/constants';

type ValidLineItem<T extends MinimumValidRegistryOrder> = WithRequiredNonNullableProperty<T['lineItems'][number], 'registryItem'>;

const isValidLineItem = <T extends MinimumValidRegistryOrder>(lineItem: T['lineItems'][number]): lineItem is ValidLineItem<T> => {
  return !!lineItem.registryItem;
};

/**
 * Orders can have multiple line items. In the gift basket, we consider each line item to an order as an
 * individual entry into the "gift tracker".
 */
export const flattenedValidRegistryOrders = <T extends MinimumValidRegistryOrder>(orders: Maybe<Array<T>>, maxEntryCount: number = 24) => {
  const entries = (orders || [])
    .flatMap(order => {
      return order.lineItems.reduce(
        (acc, lineItem) => {
          if (isValidLineItem<T>(lineItem)) {
            acc.push({ order, lineItem });
          }
          return acc;
        },
        [] as Array<{ order: T; lineItem: ValidLineItem<T> }>,
      );
    })
    .sort(({ order: order1 }, { order: order2 }) => {
      return new Date(order2.orderedAt.timestamp).valueOf() - new Date(order1.orderedAt.timestamp).valueOf();
    });

  // The new API will allow us to request a group of order ids -- this allows us to fine tune how many orders actually need to be requested
  // eg.
  //    suppose there are 100 total orders.
  //      - maxItemCount: 25
  //      - the first 5 orders have 5 valid line items each.
  //
  //    we'd only need to request 5 individual orders since they would meet our max requirement.
  const uniqueOrderIdSet: Set<string> = new Set();
  entries.slice(0, maxEntryCount).forEach(entry => {
    uniqueOrderIdSet.add(entry.order.id);
  });

  return { uniqueOrderIds: Array.from(uniqueOrderIdSet.keys()), entries };
};

export const useDeferredRedirect = () => {
  const isAdminRoute = !!useRouteMatch('/:eventHandle/edit');
  const history = useHistory();
  const { formatToUrl } = useFeatureFlagsContext();
  const deferredAdminRegManageRedirect = useCallback(
    (pathname: string, manageTab: GiftBasketTab) => {
      withWindow(global => {
        // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track-link
        // Temporary solution to defer redirect while giving any existing telemetry calls time to fire
        global.setTimeout(() => {
          if (isAdminRoute) {
            history.push(pathname, { manageTab: manageTab });
            return;
          }
          global.location.href = formatToUrl(`${global.location.origin}${pathname}?${ADMIN_MANAGE_PAGE_TAB_QUERY_PARAM}=${manageTab}`);
        }, 300);
      });
    },
    [history, isAdminRoute, formatToUrl],
  );

  return {
    deferredAdminRegManageRedirect,
  };
};
